"use client";
import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Switch,
  Chip,
} from "@mui/material";
import { Save as SaveIcon, Coronavirus as VirusIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import {
  createInfection,
  deleteInfectionById,
  getInfection,
} from "../../../../../../api/services/observations";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditInfectionModal from "./EditInfectionModal";
import CustomDateField from "../../../../../CustomForm/CustomDate";
import CustomInputSelect from "../../../../../FormFields/CustomSelect/CustomInputSelect";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import DataTable from "../../../../../Table/DataTable";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";

const infectionTypeOptions = [
  "COVID-19",
  "C.diff",
  "E.coli",
  "MRSA",
  "Norovirus",
  "UTI",
];

// 🔹 Helper — current date as "YYYY-MM-DD"
const getTodayDate = () => new Date().toISOString().slice(0, 10);

// 🔹 Helper — current time as "HH:mm" (24-hour, matches CustomTimePicker format)
const getCurrentTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

// 🔹 Initial form state with today's date + current time
const getInitialFormData = () => ({
  start_date: getTodayDate(),
  date_time: getCurrentTime(),
  infection_type: "",
  notes: "",
});

const Infection = () => {
  const queryClient = useQueryClient();
  const { id: clientId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedInfection, setSelectedInfection] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [formData, setFormData] = useState(getInitialFormData());
  const [errors, setErrors] = useState({});

  const [isCleared, setIsCleared] = useState(false);
  const [isHospitalized, setIsHospitalized] = useState(false);
  const [isIsolationRequired, setIsIsolationRequired] = useState(false);
  const [handover, setHandover] = useState(false);
  const [manager, setManager] = useState(false);

  const { data: infections, refetch } = useQuery([QK.infection, clientId], () =>
    getInfection(clientId)
  );

  const infectionPostMutation = useMutation(createInfection, {
    onSuccess: () => {
      enqueueSnackbar("Infection record created successfully", {
        variant: "success",
      });
      refetch();
    },
    onError: () =>
      enqueueSnackbar("Failed to create Infection", { variant: "error" }),
  });

  const deleteMutation = useMutation(deleteInfectionById, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.infection]);
      enqueueSnackbar("Infection deleted successfully", { variant: "success" });
      refetch();
    },
    onError: () =>
      enqueueSnackbar("Failed to delete record", { variant: "error" }),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInfectionEdit = (id) => {
    setSelectedInfection(id);
    setShowEditModal(true);
  };

  const handleDeleteClick = (id) => {
    setSelectedInfection(id);
    setShowConfirmModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};
    if (!formData.start_date) newErrors.start_date = "Start date is required";
    if (!formData.date_time) newErrors.date_time = "Time is required";
    if (!formData.infection_type)
      newErrors.infection_type = "Infection type is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      handover,
      manager,
      start_date: formData.start_date,
      date_time: `${formData.start_date}T${formData.date_time}`,
      infection_type: formData.infection_type,
      cleared: isCleared,
      hospitalized: isHospitalized,
      isolation_required: isIsolationRequired,
      notes: formData.notes,
      client: clientId,
    };

    infectionPostMutation.mutate(payload, {
      onSuccess: () => {
        // 🔹 Reset with fresh date + time
        setFormData(getInitialFormData());
        setErrors({});
        setHandover(false);
        setManager(false);
        setIsCleared(false);
        setIsHospitalized(false);
        setIsIsolationRequired(false);
      },
    });
  };

  const StatusChip = ({ label, active, activeColor }) => (
    <Chip
      label={active ? label : "No"}
      size="small"
      color={active ? activeColor : "default"}
      variant={active ? "filled" : "outlined"}
      sx={{ fontWeight: 600, minWidth: 60, height: 24, fontSize: "0.7rem" }}
    />
  );

  const headers = [
    { key: "start_date", label: "Start Date" },
    { key: "date_time", label: "Time" },
    { key: "infection_type", label: "Infection Type" },
    { key: "cleared", label: "Cleared" },
    { key: "hospitalized", label: "Hospitalized" },
    { key: "isolation_required", label: "Isolation" },
    { key: "manager", label: "Manager" },
    { key: "handover", label: "Handover" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "start_date") {
      return item.start_date || "-";
    }
    if (header.key === "date_time") {
      return item.date_time
        ? new Date(item.date_time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-";
    }
    if (header.key === "infection_type") {
      return (
        <Typography
          variant="body2"
          fontWeight={700}
          color="primary.main"
          sx={{ fontSize: "0.8rem" }}
        >
          {item.infection_type || "-"}
        </Typography>
      );
    }
    if (header.key === "cleared") {
      return (
        <StatusChip
          label="Cleared"
          active={item.cleared}
          activeColor="success"
        />
      );
    }
    if (header.key === "hospitalized") {
      return (
        <StatusChip
          label="Yes"
          active={item.hospitalized}
          activeColor="error"
        />
      );
    }
    if (header.key === "isolation_required") {
      return (
        <StatusChip
          label="Required"
          active={item.isolation_required}
          activeColor="warning"
        />
      );
    }
    if (header.key === "manager") {
      return (
        <Chip
          label={item.manager ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 600,
            height: 22,
            fontSize: "0.6rem",
            bgcolor: item.manager ? "#D1FAE5" : "#FEE2E2",
            color: item.manager ? "#039855" : "#DC2626",
          }}
        />
      );
    }
    if (header.key === "handover") {
      return (
        <Chip
          label={item.handover ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 600,
            height: 22,
            fontSize: "0.6rem",
            bgcolor: item.handover ? "#D1FAE5" : "#FEE2E2",
            color: item.handover ? "#039855" : "#DC2626",
          }}
        />
      );
    }
    return item[header.key] || "-";
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <form onSubmit={handleSubmit} id="infectionForm">
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            mb: 3,
          }}
        >
          <CardContent sx={{ py: 2, px: 2.5 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  bgcolor: "primary.main",
                  p: 0.8,
                  borderRadius: 2,
                  display: "flex",
                }}
              >
                <VirusIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={700}
                fontSize="1.1rem"
                color="text.primary"
              >
                New Infection Observation
              </Typography>
            </Stack>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ flex: "1 1 30%", minWidth: "200px" }}>
                <CustomDateField
                  label="Start Date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  error={!!errors.start_date}
                  helperText={errors.start_date}
                  required
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: "1 1 30%", minWidth: "200px" }}>
                <CustomTimePicker
                  label="Time"
                  name="date_time"
                  value={formData.date_time}
                  onChange={handleChange}
                  error={!!errors.date_time}
                  helperText={errors.date_time}
                  required
                  size="small"
                  fontSize="0.8rem"
                />
              </Box>
              <Box sx={{ flex: "1 1 30%", minWidth: "200px" }}>
                <CustomInputSelect
                  label="Infection Type *"
                  name="infection_type"
                  options={infectionTypeOptions}
                  selectedValue={formData.infection_type}
                  onChange={handleChange}
                  error={!!errors.infection_type}
                  helperText={errors.infection_type}
                  placeholder="Select Infection Type"
                  initialLabel
                  required
                  fullWidth
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
              <Box sx={{ flex: "1 1 48%", minWidth: "280px" }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    borderStyle: "dashed",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Clinical Indicators
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={isCleared}
                          onChange={(e) => setIsCleared(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Infection is cleared
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={isHospitalized}
                          onChange={(e) => setIsHospitalized(e.target.checked)}
                          color="error"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Result in hospitalisation?
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={isIsolationRequired}
                          onChange={(e) =>
                            setIsIsolationRequired(e.target.checked)
                          }
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Isolation required
                        </Typography>
                      }
                    />
                  </Box>
                </Paper>
              </Box>
              <Box sx={{ flex: "1 1 48%", minWidth: "280px" }}>
                <CustomTextArea
                  label="Clinical Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describe the symptoms or observation..."
                  fullWidth
                  size="small"
                  sx={{ height: "100%" }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={2}>
                <CustomCheckbox
                  label="Notify Manager"
                  checked={manager}
                  onClick={(checked) => setManager(checked)}
                />
                <CustomCheckbox
                  label="Add to Handover"
                  checked={handover}
                  onClick={(checked) => setHandover(checked)}
                />
              </Stack>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                startIcon={<SaveIcon />}
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.85rem",
                }}
              >
                Save Observation
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>

      <Typography
        variant="subtitle1"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          color: "text.secondary",
          px: 1,
          fontSize: "1rem",
        }}
      >
        Recent Observations
      </Typography>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          border: "1px solid #eef2f6",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={infections || []}
            headers={headers}
            onUpdate={handleInfectionEdit}
            onDelete={handleDeleteClick}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={false}
            size="small"
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={() => {
          deleteMutation.mutate(selectedInfection);
          setShowConfirmModal(false);
        }}
        title="Delete Record?"
        body="Are you sure you want to delete this infection observation? This action cannot be undone."
      />

      {selectedInfection && (
        <EditInfectionModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          selectedInfection={selectedInfection}
          refetch={refetch}
        />
      )}
    </Box>
  );
};

export default Infection;